import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { PlannerPublishDeleteDialog } from './planner-publish-delete-dialog';
import { PlannerPublishDialog } from './planner-publish-dialog';
import { PlannerEnv } from '@/utils/env';
import { useUser } from '@/components/providers/user-provider';
import { useQueryClient } from '@tanstack/react-query';
import { formattedDate } from '@/utils/date';
import { DiffIcon, Trash2, UploadCloud } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { PlannerConfigEntity } from '@/services/configurator/PlannerConfigTypes';
import { Link, useNavigate } from 'react-router-dom';
import { PARAMS } from '@/utils/params';
import JsonDiffViewer from '@/components/common/DiffViewer/JsonDiffViewer';
import usePlannerPublish from './usePlannerPublish';

export type PlannerPublisStagingList = {
  id: string;
  value: string;
  date: string;
  modifiedBy: string;
};

export type PlannerPublishListProps = {
  open: boolean;
  setOpen: (val: boolean) => void;
  publishToEnv: PlannerEnv;
};

export function PlannerPublishList(props: PlannerPublishListProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { role } = useUser();
  const {
    stagingList,
    allSelected,
    setAllSelected,
    publishAllLoading,
    deleteAllLoading,
    deleteAllSelected,
    publishAllSelected,
    current,
    setCurrent,
    prev,
    setPrev,
    getPlannerData,
    updateList,
    setDiffPlannerId,
    diffPlannerId,
    stagingData,
  } = usePlannerPublish(props.publishToEnv);

  useEffect(() => {
    if (!props.open) {
      return;
    }

    updateList();
  }, [props.open]);

  return (
    <div className='overflow-y-scroll pe-4' style={{ maxHeight: '75vh' }}>
      {diffPlannerId ? (
        <>
          <div className='flex justify-end items-center p-2'>
            <Button
              onClick={() => {
                setDiffPlannerId('');
                setPrev({} as PlannerConfigEntity);
                setCurrent({} as PlannerConfigEntity);
              }}
            >
              Back
            </Button>
          </div>
          <JsonDiffViewer previous={prev} current={current} />
        </>
      ) : (
        <>
          <div className='flex items-center justify-between'>
            <div>
              <Checkbox
                id='select-all-checkbox'
                onCheckedChange={(checked) => {
                  if (checked) {
                    setAllSelected(stagingList.map((s) => s.id));
                  } else {
                    setAllSelected([]);
                  }
                }}
              ></Checkbox>{' '}
              <label htmlFor='select-all-checkbox'>Select all</label>
            </div>
            <div>
              {role === 'admin' && (
                <Button
                  onClick={deleteAllSelected}
                  isLoading={deleteAllLoading}
                  icon={<Trash2 />}
                  variant='destructive'
                  className='mr-2'
                >
                  Delete all selected
                </Button>
              )}
              <Button
                onClick={publishAllSelected}
                isLoading={publishAllLoading}
                icon={<UploadCloud />}
              >
                Publish all selected
              </Button>
            </div>
          </div>
          <div className='border-solid border-b-2 my-4'></div>
          {stagingList.map((item) => {
            return (
              <div key={item.id} className='grid grid-cols-7 gap-4 mb-1'>
                <span className='col-span-2 flex gap-2'>
                  <Checkbox
                    id={item.id}
                    checked={allSelected.includes(item.id)}
                    onCheckedChange={(isChecked) => {
                      if (isChecked) {
                        setAllSelected((prev) => [...prev, item.id]);
                      } else {
                        setAllSelected(
                          allSelected.filter((id) => id !== item.id),
                        );
                      }
                    }}
                  />
                  <label htmlFor={item.id}>{item.value}</label>
                </span>
                <span className='col-span-1'>{formattedDate(item.date)}</span>
                {props.publishToEnv === 'cte' ? (
                  <Link
                    to={`/planner/draft?${PARAMS.FROM_DRAFT_ID}=${item.id}`}
                    state={getPlannerData(item.id, stagingData)}
                    className='gap-2 inline-flex w-full'
                  >
                    <Button
                      className='gap-2 h-8 border-solid border-2 rounded p-4 w-full'
                      variant='outline'
                    >
                      <DiffIcon className='h-4 w-4 min-[800px]:hidden' />
                      <span className='max-[800px]:hidden'>Edit</span>
                    </Button>
                  </Link>
                ) : (
                  <Button
                    className='gap-2 h-8 border-solid border-2 rounded p-4 w-full'
                    variant='outline'
                    disabled
                  >
                    <DiffIcon className='h-4 w-4 min-[800px]:hidden' />
                    <span className='max-[800px]:hidden'>Edit</span>
                  </Button>
                )}
                <Button
                  onClick={() => setDiffPlannerId(item.id)}
                  className='gap-2 h-8 border-solid border-2 rounded p-4'
                  variant='outline'
                >
                  <DiffIcon className='h-4 w-4 min-[800px]:hidden' />
                  <span className='max-[800px]:hidden'>Diff</span>
                </Button>
                <PlannerPublishDialog
                  publishToEnv={props.publishToEnv}
                  name={item.value}
                  id={item.id}
                  onPublish={async () => {
                    updateList();

                    await queryClient.invalidateQueries({
                      queryKey: ['get-planners'],
                    });

                    props.setOpen(false);
                    navigate('/');
                  }}
                />
                <PlannerPublishDeleteDialog
                  name={item.value}
                  id={item.id}
                  onDelete={() => updateList()}
                />
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
